import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { router } from '@inertiajs/react'
import { toast } from 'sonner'
import {
  ChevronDown, ChevronRight, ScanLine, PlusCircle, CheckCircle2,
  CloudOff, Loader2, Cloud, Camera, X, StickyNote, Trash2,
  History, Ban, RotateCcw, Truck, MessageSquare, Search, Link2, Pencil, Check,
} from 'lucide-react'
import DashboardLayout from '../../layouts/DashboardLayout'
import { cn } from '../../lib/utils'

// CSRF for raw-fetch autosave (Inertia's router can't be used here — the autosave endpoint returns
// JSON, not an Inertia response). On the practice LAN/Tailscale CSRF is exempt; off-LAN we send the
// meta token. Either way the count is never lost — unsynced changes stay queued in localStorage.
function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
}

// text-base (16px), never smaller: iPad/iPhone Safari auto-zooms the page into any input under 16px (stockview1023).
const INPUT = 'w-full rounded-xl border border-brand-border bg-white px-3 py-2 text-base text-brand-ink focus:border-brand-primary focus:outline-none'

export default function Count({ take, areas, categories = [], suppliers = [], is_owner }) {
  const finished = take.status === 'finished'
  const queueKey = `stocktake:${take.id}:queue`
  // Flat working copy of all entries, keyed by id. REPLAY (stockresume1023): edits queued on THIS device that never
  // reached the server (Back arrow / tab closed / offline) are laid over the server values, so the screen shows what
  // the counter typed; the normal flush then sends them once (the server write is idempotent: same value, same row).
  const initial = useMemo(() => {
    const m = {}
    areas.forEach((a) => a.entries.forEach((e) => { m[e.id] = { ...e } }))
    const pending = loadQueue(queueKey)
    Object.keys(pending).forEach((id) => { if (m[id]) m[id] = { ...m[id], ...pending[id] } })
    return m
  }, [areas, queueKey])
  const [rows, setRows] = useState(initial)
  // The area list is local state so "Add item" can drop the new row straight into the CURRENT take.
  const [areaList, setAreaList] = useState(areas)
  const [supplierList, setSupplierList] = useState(suppliers)
  const [editItem, setEditItem] = useState(null)           // entry id whose item editor is open
  const [open, setOpen] = useState(() => Object.fromEntries(areas.map((a) => [a.area, true])))
  const [sync, setSync] = useState('saved') // saved | saving | offline
  const [lastSaved, setLastSaved] = useState(null) // timestamp of the last successful sync
  const [flagOpen, setFlagOpen] = useState(false)
  const [scanOpen, setScanOpen] = useState(false)
  const [prefillName, setPrefillName] = useState('')
  const [linkCode, setLinkCode] = useState(null)           // scanned barcode with no match → link-to-existing sheet
  const [uploadingItem, setUploadingItem] = useState(null) // stock_item_id currently uploading a photo
  const [photoView, setPhotoView] = useState(null)         // entry id whose photo gallery is open full-screen
  const [notesView, setNotesView] = useState(null)         // entry id whose notes editor is open
  const [historyView, setHistoryView] = useState(null)     // entry id whose history timeline is open
  const [query, setQuery] = useState('')                   // live text filter across the whole count
  const [activeCat, setActiveCat] = useState('')           // '' = all categories; else a category chip

  const queueRef = useRef(loadQueue(queueKey))
  const failRef = useRef(0)
  const rowRefs = useRef({})
  // ONE hidden camera input for the whole page (not 193 of them). `capture="environment"` is what
  // makes the tablet open its rear camera straight away instead of a file browser.
  const cameraRef = useRef(null)
  const shootTargetRef = useRef(null)
  // Keyboard-safe typing on the tablet (stockview1023): the focused count field is kept between the sticky header
  // and the on-screen keyboard. `kb.pad` = extra bottom room while the keyboard is up; `kb.open` hides the thumb bar.
  const listRef = useRef(null)
  const headerRef = useRef(null)
  const kb = useKeyboardSafeFocus(listRef, headerRef)

  const counted = Object.values(rows).filter((r) => r.counted).length
  const total = Object.values(rows).length

  // ── search + category filter ──────────────────────────────────────────────
  // A pure client-side filter over the already-loaded rows — no server round-trip, so it stays instant
  // even on a ~200-item count. Search spans the fields a counter would recognise the item by; the
  // category chips narrow to one section. The two compose (search within a chosen category).
  const q = query.trim().toLowerCase()
  const filtering = q !== '' || activeCat !== ''
  function matchesQuery(r) {
    if (!q) return true
    return `${r.name || ''} ${r.ref || ''} ${r.supplier_code || ''} ${r.barcode || ''} ${r.supplier || ''}`
      .toLowerCase().includes(q)
  }
  // Entries of an area that survive the active filters, read from the LIVE row values (so a just-typed
  // count/cancel doesn't fall out of the list mid-edit).
  function visibleEntries(a) {
    if (activeCat && a.area !== activeCat) return []
    return a.entries.filter((e) => matchesQuery(rows[e.id] || e))
  }
  const matchCount = filtering ? areaList.reduce((n, a) => n + visibleEntries(a).length, 0) : total

  // ── offline-resilient autosave ────────────────────────────────────────────
  // `keepalive` (stockresume1023): when the page is being LEFT (Back arrow, Inertia link, tab hidden/closed) the
  // browser still delivers the PATCH after the page is gone. keepalive bodies are capped at 64 KB, so a huge
  // queue goes as a normal fetch (it stays queued in localStorage either way).
  const flush = useCallback(async (opts = {}) => {
    const q = queueRef.current
    const ids = Object.keys(q)
    if (ids.length === 0) { setSync('saved'); return }
    if (!navigator.onLine) { setSync('offline'); return }
    setSync('saving')
    const batch = ids.map((id) => ({ id: Number(id), ...q[id] }))
    const body = JSON.stringify({ entries: batch })
    try {
      const res = await fetch(`/stock_takes/${take.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken(), 'Accept': 'application/json' },
        body,
        credentials: 'same-origin',
        keepalive: !!opts.keepalive && body.length < 60000,
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      // clear only what we sent (new edits during the request stay queued)
      batch.forEach((b) => { if (shallowEqual(queueRef.current[b.id], stripId(b))) delete queueRef.current[b.id] })
      saveQueue(queueKey, queueRef.current)
      failRef.current = 0
      const cleared = Object.keys(queueRef.current).length === 0
      if (cleared) setLastSaved(new Date())
      setSync(cleared ? 'saved' : 'saving')
    } catch (e) {
      // A failed save must NEVER look benign (this caused a lost count on 2026-06-24). After 2 misses
      // flip to a LOUD error state so staff KNOW the counts aren't on the server yet. Counts stay
      // safe in localStorage and keep retrying — but the UI no longer pretends everything is fine.
      failRef.current += 1
      setSync(!navigator.onLine ? 'offline' : (failRef.current >= 2 ? 'error' : 'saving'))
    }
  }, [take.id, queueKey])

  // debounce flush after edits (<= 1 s) + retry on reconnect + safety interval
  useEffect(() => {
    const t = setTimeout(flush, 800)
    return () => clearTimeout(t)
  }, [rows, flush])

  // FLUSH ON LEAVE (stockresume1023, Paul 23 Oct: "if we press the arrow at the top left of the browser and we go
  // out, the stock take should be saved"). beforeunload alone is unreliable (Inertia navigation and mobile tab close
  // don't fire it), so flush with keepalive on: an Inertia visit starting (router 'before'), pagehide,
  // visibilitychange -> hidden, and the page unmounting (Back arrow inside the app).
  useEffect(() => {
    const leave = () => { if (Object.keys(queueRef.current).length) flush({ keepalive: true }) }
    const onVisibility = () => { if (document.visibilityState === 'hidden') leave() }
    window.addEventListener('pagehide', leave)
    document.addEventListener('visibilitychange', onVisibility)
    const offBefore = router.on('before', () => { leave() })
    return () => {
      window.removeEventListener('pagehide', leave)
      document.removeEventListener('visibilitychange', onVisibility)
      if (typeof offBefore === 'function') offBefore()
      leave()
    }
  }, [flush])
  useEffect(() => {
    const on = () => flush()
    const off = () => setSync('offline')
    window.addEventListener('online', on)
    window.addEventListener('offline', off)
    const iv = setInterval(flush, 15000)
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); clearInterval(iv) }
  }, [flush])

  // Guard against leaving the page with counts that haven't reached the server yet (the data-loss trap).
  useEffect(() => {
    const warn = (ev) => { if (Object.keys(queueRef.current).length) { ev.preventDefault(); ev.returnValue = '' } }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [])

  function enqueue(id, patch) {
    queueRef.current[id] = { ...(queueRef.current[id] || {}), ...patch }
    saveQueue(queueKey, queueRef.current)
    setSync('saving')
  }

  function setInStock(e, value) {
    const par = parseFloat(e.par_level || '0')
    const inStock = value === '' ? null : value
    const n = parseFloat(value)
    // auto-fill to_order = how many to reach par, unless the user typed their own
    const autoOrder = (value === '' || isNaN(n)) ? '' : String(Math.max(par - n, 0))
    setRows((r) => {
      const cur = r[e.id]
      const keepOrder = cur._orderTouched ? cur.to_order : autoOrder
      return { ...r, [e.id]: { ...cur, in_stock: inStock, to_order: keepOrder, counted: value !== '' } }
    })
    enqueue(e.id, { in_stock: inStock, to_order: (rows[e.id]?._orderTouched ? rows[e.id].to_order : autoOrder), counted: value !== '' })
  }

  function setToOrder(e, value) {
    setRows((r) => ({ ...r, [e.id]: { ...r[e.id], to_order: value, _orderTouched: true } }))
    enqueue(e.id, { to_order: value })
  }

  // Month/year of expiry (value = "YYYY-MM" from the month picker).
  function setExpires(e, value) {
    setRows((r) => ({ ...r, [e.id]: { ...r[e.id], expires_on: value } }))
    enqueue(e.id, { expires_on: value })
  }

  // Stock (supplier) code — fill in any blanks so we order the exact right product; persists to the catalogue item.
  function setSupplierCode(e, value) {
    setRows((r) => ({ ...r, [e.id]: { ...r[e.id], supplier_code: value } }))
    enqueue(e.id, { supplier_code: value })
  }

  // Par (min stock level we like to keep) — EDITABLE by the counter at ANY stage, even on a finished count
  // (par is a catalogue property, not a count value, so it's never locked). Persists to the item + is logged
  // server-side. Blank clears to 0. The "To order" auto-fill uses par, so a fresh par re-drives the suggestion.
  function setPar(e, value) {
    setRows((r) => ({ ...r, [e.id]: { ...r[e.id], par_level: value } }))
    enqueue(e.id, { par_level: value })
  }


  // Stepper: ±1 quick-adjust (research: a hybrid stepper + keypad is faster + lower-error than a bare
  // keypad for small counts on a touchscreen). Clamps at 0; reuses setInStock so autosave + to_order
  // auto-fill + counted flag all apply identically to typing.
  function bump(e, delta) {
    const cur = parseFloat(rows[e.id]?.in_stock)
    const base = isNaN(cur) ? 0 : cur
    setInStock(e, String(Math.max(base + delta, 0)))
  }

  // Par stepper — the same ± feel as In stock (Paul 23 Oct 2026). Par is a catalogue value: editable at ANY stage.
  function bumpPar(e, delta) {
    const cur = parseFloat(rows[e.id]?.par_level)
    const base = isNaN(cur) ? 0 : cur
    setPar(e, String(Math.max(base + delta, 0)))
  }

  // Quick-pick numbers (Paul 23 Oct 2026: "a 1, 2, 3 that I can just click directly instead of plus and minus").
  // One tap sets the value; it goes through the SAME setter as typing, so autosave / to_order / counted all apply.
  const QUICK_COUNTS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]
  // A render HELPER (called as a function, not as a component) so React never remounts it per keystroke.
  function quickPick(value, onPick, disabled, label) {
    return (
      <div className="mt-1.5 flex flex-wrap gap-1" role="group" aria-label={label}>
        {QUICK_COUNTS.map((n) => {
          const on = String(value ?? '') !== '' && parseFloat(value) === n
          return (
            <button key={n} type="button" disabled={disabled} onClick={() => onPick(String(n))} aria-pressed={on}
              className={`h-9 min-w-[2.25rem] rounded-lg border px-2 text-sm font-semibold active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary disabled:opacity-40 ${on ? 'border-brand-primary bg-brand-primary text-white' : 'border-brand-border bg-white text-brand-ink'}`}>{n}</button>
          )
        })}
      </div>
    )
  }

  // ── item photo: tap the tile → tablet camera → resize → upload ────────────
  // The photo belongs to the CATALOGUE ITEM, not this count: photograph the box once and it shows on
  // every future count and in the ordering export ("is this the right item?"). A retake keeps the old
  // one server-side, so a stray tap can never destroy a good photo.
  function openCamera(entry) {
    shootTargetRef.current = entry
    if (cameraRef.current) { cameraRef.current.value = ''; cameraRef.current.click() }
  }

  async function onPhotoPicked(ev) {
    const file = ev.target.files?.[0]
    const entry = shootTargetRef.current
    shootTargetRef.current = null
    if (!file || !entry) return

    // A photo can't ride the offline localStorage queue the way a number can (it's a binary blob, and
    // a queued 150 KB image per item would blow the 5 MB localStorage budget). So say so PLAINLY
    // rather than pretending it saved — the same rule the count autosave learned on 2026-06-24.
    if (!navigator.onLine) { toast.error("You're offline — the photo can't be saved. Take it again once you're back on WiFi."); return }

    setUploadingItem(entry.stock_item_id)
    try {
      const blob = await downscale(file)
      const fd = new FormData()
      fd.append('photo', blob, 'stock.jpg')
      const res = await fetch(`/stock_items/${entry.stock_item_id}/photo`, {
        method: 'POST', headers: { 'X-CSRF-Token': csrfToken(), Accept: 'application/json' }, body: fd,
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.ok) throw new Error(data.error || `HTTP ${res.status}`)
      // The photo is on the ITEM, so update EVERY row showing that item (the same item can appear in
      // more than one row only via a data quirk, but keep them in step regardless).
      setRows((prev) => {
        const next = { ...prev }
        Object.values(prev).forEach((r) => {
          if (r.stock_item_id === entry.stock_item_id) {
            next[r.id] = { ...r, photo_url: data.photo_url, photos: data.photos, photo_taken_at: data.photo_taken_at, photo_taken_by: data.photo_taken_by }
          }
        })
        return next
      })
      toast.success('Photo saved')
    } catch (err) {
      toast.error(`Photo not saved — ${err.message || 'try again'}`)
    } finally {
      setUploadingItem(null)
    }
  }

  // ── item photo delete — ANY staff member while a count is open, the owner always (Paul 23 Oct 2026). Confirmed
  // here, logged + audited server-side. The gallery's per-photo X. ──
  async function deletePhoto(entry, photoId) {
    if (!entry) return
    if (!confirm('Delete this photo? It will be logged.')) return
    if (!navigator.onLine) { toast.error("You're offline — try again once you're back on WiFi."); return }
    try {
      const res = await fetch(`/stock_items/${entry.stock_item_id}/photo/${photoId}`, {
        method: 'DELETE', headers: { 'X-CSRF-Token': csrfToken(), Accept: 'application/json' },
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.ok) throw new Error(data.error || `HTTP ${res.status}`)
      // Mirror the photo fan-out: every row of this item gets the returned (shrunk) photos[].
      setRows((prev) => {
        const next = { ...prev }
        Object.values(prev).forEach((r) => {
          if (r.stock_item_id === entry.stock_item_id) {
            next[r.id] = { ...r, photo_url: data.photo_url, photos: data.photos }
          }
        })
        return next
      })
      toast.success('Photo removed')
    } catch (err) {
      toast.error(`Could not remove — ${err.message || 'try again'}`)
    }
  }

  // ── item details (Paul 23 Oct 2026: "Anyone should be able to edit the supplier" … "everything must be editable").
  // ANY staff member, from the count page: PATCH /stock_items/:id/details (audited old → new server-side). The
  // fields live on the CATALOGUE item, so the saved values fan out to every row of that item. Pending count
  // edits are flushed FIRST so a queued par/code can never land on top of what was just saved here. ──
  const [savedItem, setSavedItem] = useState(null) // stock_item_id that just saved (shows a ✓)
  async function saveDetails(entry, patch) {
    if (!navigator.onLine) { toast.error("You're offline — the item can't be saved yet."); return false }
    await flush()
    try {
      const res = await fetch(`/stock_items/${entry.stock_item_id}/details`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken(), Accept: 'application/json' },
        body: JSON.stringify({ stock_item: patch }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.ok) throw new Error(data.error || `HTTP ${res.status}`)
      const it = data.item || {}
      setRows((prev) => {
        const out = { ...prev }
        Object.values(prev).forEach((r) => {
          if (r.stock_item_id === entry.stock_item_id) {
            out[r.id] = { ...r, name: it.name, ref: it.ref, unit: it.unit, barcode: it.barcode,
              supplier: it.supplier, supplier_code: it.supplier_code, par_level: it.par_level,
              category: it.category_label, item_category: it.category, storage_area: it.storage_area,
              logs: it.logs ?? r.logs }
          }
        })
        return out
      })
      // A CATEGORY change moves the row under its new heading now, not after a reload (stockfix1023b, Codex 6).
      if ('category' in patch) {
        const toArea = it.category_label || it.category || 'Uncategorised'
        setAreaList((prev) => regroupItemRows(prev, entry.stock_item_id, toArea))
        setOpen((o) => ({ ...o, [toArea]: true }))
      }
      if (it.supplier) setSupplierList((l) => (l.includes(it.supplier) ? l : [...l, it.supplier].sort((a, b) => a.localeCompare(b))))
      setSavedItem(entry.stock_item_id)
      setTimeout(() => setSavedItem((cur) => (cur === entry.stock_item_id ? null : cur)), 2500)
      return true
    } catch (err) {
      toast.error(`Not saved — ${err.message || 'try again'}`)
      return false
    }
  }

  // "Add item" on a count lands in THIS take at once (Paul 23 Oct 2026): the server created the item (or found it
  // already in the catalogue) AND its entry on this take; drop the row at the TOP of its section, open + show it.
  function addEntryToTake(entry, area) {
    if (!entry) return
    const areaName = area || entry.category || 'Uncategorised'
    setRows((prev) => (prev[entry.id] ? prev : { ...prev, [entry.id]: { ...entry } }))
    setAreaList((prev) => {
      if (prev.some((a) => a.entries.some((e) => e.id === entry.id))) return prev
      const idx = prev.findIndex((a) => a.area === areaName)
      if (idx === -1) return [{ area: areaName, entries: [entry] }, ...prev]
      return prev.map((a, i) => (i === idx ? { ...a, entries: [entry, ...a.entries] } : a))
    })
    setQuery(''); setActiveCat('')
    setOpen((o) => ({ ...o, [areaName]: true }))
    setTimeout(() => {
      const el = rowRefs.current[entry.id]
      if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.querySelector('input')?.focus() }
    }, 150)
  }

  // ── item notes: everyday counter action (NOT owner-gated). Autosaved from NotesEditor, needs the
  // network like a photo. The note lives on the CATALOGUE item, so update every row of that item. ──
  async function saveNotes(stockItemId, text) {
    if (!navigator.onLine) { toast.error("You're offline — the note can't be saved yet."); return false }
    try {
      const res = await fetch(`/stock_items/${stockItemId}/notes`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken(), Accept: 'application/json' },
        body: JSON.stringify({ notes: text }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.ok) throw new Error(data.error || `HTTP ${res.status}`)
      setRows((prev) => {
        const next = { ...prev }
        Object.values(prev).forEach((r) => {
          if (r.stock_item_id === stockItemId) {
            next[r.id] = { ...r, notes: data.notes, notes_updated_at: data.notes_updated_at, notes_updated_by: data.notes_updated_by }
          }
        })
        return next
      })
      return true
    } catch (err) {
      toast.error(`Note not saved — ${err.message || 'try again'}`)
      return false
    }
  }

  // ── cancel / restore an item (reversible, EVERYONE). Cancelled rows are struck through and sink to the
  // bottom of their area; toggling back restores them. We trust the server's `cancelled` + `logs`, never
  // body text, and fan the new state out to every row of this catalogue item, then re-sort. ──
  async function toggleCancel(entry) {
    if (!navigator.onLine) { toast.error("You're offline — try again once you're back on WiFi."); return }
    const next = !entry.cancelled
    try {
      const res = await fetch(`/stock_items/${entry.stock_item_id}/cancellation`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken(), Accept: 'application/json' },
        body: JSON.stringify({ cancelled: next }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.ok) throw new Error(data.error || `HTTP ${res.status}`)
      setRows((prev) => {
        const out = { ...prev }
        Object.values(prev).forEach((r) => {
          if (r.stock_item_id === entry.stock_item_id) {
            out[r.id] = { ...r, cancelled: data.cancelled, cancelled_at: data.cancelled_at, cancelled_by: data.cancelled_by, logs: data.logs ?? r.logs }
          }
        })
        return out
      })
      toast.success(data.cancelled ? 'Item cancelled' : 'Item restored')
    } catch (err) {
      toast.error(`Could not update — ${err.message || 'try again'}`)
    }
  }

  // ── per-photo note (EVERYONE). Autosaved from the PhotoViewer; the note lives on the photo
  // (attachment), so the returned photos[] is fanned out to every row of this catalogue item. ──
  async function savePhotoNote(stockItemId, photoId, note) {
    if (!navigator.onLine) { toast.error("You're offline — the note can't be saved yet."); return false }
    try {
      const res = await fetch(`/stock_items/${stockItemId}/photo/${photoId}/note`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken(), Accept: 'application/json' },
        body: JSON.stringify({ note }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.ok) throw new Error(data.error || `HTTP ${res.status}`)
      setRows((prev) => {
        const out = { ...prev }
        Object.values(prev).forEach((r) => {
          if (r.stock_item_id === stockItemId) { out[r.id] = { ...r, photos: data.photos } }
        })
        return out
      })
      return true
    } catch (err) {
      toast.error(`Note not saved — ${err.message || 'try again'}`)
      return false
    }
  }

  // ── barcode: passive USB-scanner (keyboard wedge) + camera button ─────────
  useEffect(() => {
    let buf = '', last = 0
    const onKey = (ev) => {
      if (flagOpen || scanOpen) return
      const tag = (ev.target.tagName || '').toLowerCase()
      const now = Date.now()
      if (now - last > 100) buf = ''        // scanners type fast; humans don't
      last = now
      if (ev.key === 'Enter') { if (buf.length >= 4) { matchBarcode(buf); ev.preventDefault() } buf = ''; return }
      if (ev.key.length === 1 && tag !== 'input') buf += ev.key
      else if (ev.key.length === 1 && tag === 'input') buf += ev.key // allow scanning into a focused field too
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [rows, flagOpen, scanOpen])

  function matchBarcode(code) {
    const hit = Object.values(rows).find((r) => (r.barcode || '').trim() === code.trim())
    if (hit) {
      const area = areaOf(hit.id)
      if (area) setOpen((o) => ({ ...o, [area]: true }))
      setTimeout(() => {
        const el = rowRefs.current[hit.id]
        if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.querySelector('input')?.focus() }
      }, 60)
      toast.success(`Found: ${hit.name}`)
    } else {
      // Not on any item yet → the ENROLL path: link this code to the existing item it belongs to (fills
      // the catalogue over normal counts). Adding a brand-new item is still one tap away in the sheet.
      setLinkCode(code.trim())
      toast.message('No match yet — link it to an item?')
    }
  }
  function areaOf(id) { return areaList.find((a) => a.entries.some((e) => e.id === id))?.area }

  // Attach a scanned barcode to an EXISTING item. The server gates collisions (one barcode = one item);
  // on a 409 clash we return the conflicting item so the sheet can offer an explicit reassign. On success
  // we fan the new barcode to every row of that item, clear it off any item it was reassigned from, then
  // jump to the freshly-linked item so the counter sees it worked.
  async function linkBarcodeTo(itemId, force = false) {
    if (!linkCode) return
    if (!navigator.onLine) { toast.error("You're offline — try again once you're back on WiFi."); return { error: true } }
    try {
      const res = await fetch(`/stock_items/${itemId}/barcode`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken(), Accept: 'application/json' },
        body: JSON.stringify({ barcode: linkCode, force }),
      })
      const data = await res.json().catch(() => ({}))
      if (res.status === 409 && data.conflict) return { conflict: data.conflict_item }
      if (!res.ok || !data.ok) throw new Error(data.error || `HTTP ${res.status}`)
      const code = linkCode
      setRows((prev) => {
        const out = { ...prev }
        Object.values(prev).forEach((r) => {
          if (r.stock_item_id === itemId) out[r.id] = { ...r, barcode: data.barcode, logs: data.logs ?? r.logs }
          else if ((r.barcode || '').trim() === code) out[r.id] = { ...r, barcode: '' } // moved away on a forced reassign
        })
        return out
      })
      setLinkCode(null)
      toast.success('Barcode linked')
      setTimeout(() => matchBarcode(code), 80) // jump to the item we just linked
      return { ok: true }
    } catch (err) {
      toast.error(`Could not link — ${err.message || 'try again'}`)
      return { error: true }
    }
  }

  // Jump to the first still-uncounted item (research: a "next uncounted" control cuts navigation effort
  // on a long count list). Opens its area, scrolls it into view, focuses the input.
  function jumpToNextUncounted() {
    for (const a of areaList) {
      for (const e of a.entries) {
        if (!rows[e.id]?.counted) {
          setOpen((o) => ({ ...o, [a.area]: true }))
          setTimeout(() => {
            const el = rowRefs.current[e.id]
            if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.querySelector('input')?.focus() }
          }, 60)
          return
        }
      }
    }
    toast.success('All items counted 🎉')
  }

  function finishTake() {
    if (Object.keys(queueRef.current).length) { toast.error('Still saving — try again in a moment.'); flush(); return }
    // Expiry is OPTIONAL — it must NEVER block finishing a count (Paul, 2026-06-24). Missing expiries
    // are just surfaced for review later in the catalogue.
    if (!confirm(`Finish stock take #${take.id}? ${counted}/${total} items counted. This sends it to the ordering pull.`)) return
    router.post(`/stock_takes/${take.id}/finish`, {}, {
      onSuccess: () => { localStorage.removeItem(queueKey) },
      onError: () => toast.error('Could not finish the count'),
    })
  }

  return (
    <DashboardLayout>
      <div className="mx-auto max-w-3xl pb-28" style={kb.pad ? { paddingBottom: `calc(7rem + ${kb.pad}px)` } : undefined}>
        {/* sticky header: progress + sync + finish */}
        <div ref={headerRef} className="sticky top-16 z-10 -mx-4 mb-4 border-b border-brand-border bg-brand-surface/95 px-4 py-3 backdrop-blur sm:-mx-6 sm:px-6">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h1 className="flex items-center gap-2 text-lg font-semibold text-brand-ink">
                Stock Take #{take.id}
                {take.scoped && (
                  <span className="rounded-full bg-brand-primary/10 px-2 py-0.5 text-xs font-semibold text-brand-primary">{take.scope_label}</span>
                )}
              </h1>
              <p className="text-xs text-brand-muted">{take.counted_by}{finished ? ' · finished' : ''}</p>
            </div>
            <div className="flex items-center gap-2">
              <SyncBadge state={finished ? 'saved' : sync} at={lastSaved} pending={Object.keys(queueRef.current).length} />
              {!finished && counted < total && (
                <button onClick={jumpToNextUncounted} className="rounded-full border border-brand-border bg-white px-3 py-1 text-xs font-medium text-brand-ink active:scale-95">Next uncounted ↓</button>
              )}
              <span className="rounded-full bg-white px-3 py-1 text-sm font-semibold text-brand-ink shadow-sm">{counted}/{total}</span>
            </div>
          </div>
          {/* search + category chips — find any item fast on a long count; narrow to one section. */}
          <div className="mt-3 space-y-2">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" />
              <input value={query} onChange={(e) => setQuery(e.target.value)} inputMode="search"
                placeholder="Search item, code or barcode…"
                className="w-full rounded-xl border border-brand-border bg-white py-2.5 pl-9 pr-9 text-base text-brand-ink focus:border-brand-primary focus:outline-none" />
              {query && (
                <button type="button" onClick={() => setQuery('')} aria-label="clear search"
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1 text-brand-muted hover:bg-brand-surface active:scale-90">
                  <X size={15} />
                </button>
              )}
            </div>
            {areaList.length > 1 && (
              <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-0.5 sm:-mx-6 sm:px-6">
                <Chip label="All" count={total} active={activeCat === ''} onClick={() => setActiveCat('')} />
                {areaList.map((a) => {
                  const done = a.entries.filter((e) => rows[e.id]?.counted).length
                  return (
                    <Chip key={a.area} label={a.area} count={a.entries.length} done={done}
                      active={activeCat === a.area} onClick={() => setActiveCat(activeCat === a.area ? '' : a.area)} />
                  )
                })}
              </div>
            )}
          </div>
          {!finished && (
            /* Desktop: actions in the header. Mobile: a thumb-zone bottom bar (see below). */
            <div className="mt-3 hidden gap-2 lg:flex">
              <button onClick={() => setScanOpen(true)} className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-brand-border bg-white py-2.5 text-sm font-medium text-brand-ink active:scale-[.99]">
                <ScanLine size={16} /> Scan
              </button>
              <button onClick={() => { setPrefillName(''); setFlagOpen(true) }} className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-brand-border bg-white py-2.5 text-sm font-medium text-brand-ink active:scale-[.99]">
                <PlusCircle size={16} /> Add item
              </button>
              <button onClick={finishTake} className="flex items-center justify-center gap-2 rounded-xl bg-brand-primary px-4 py-2.5 text-sm font-semibold text-white active:scale-[.99]">
                <CheckCircle2 size={16} /> Finish
              </button>
            </div>
          )}
        </div>

        {sync === 'error' && !finished && (
          <div className="mb-4 rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-sm font-medium text-red-800">
            ⚠ Your counts are <b>NOT saving to the server</b> right now. They're held safely on this device and keep retrying — but <b>do not close this page</b> until the badge shows “saved”. If it stays red, check the connection or tell Paul.
          </div>
        )}
        {/* areas */}
        <div ref={listRef} className="space-y-3">
          {areaList.map((a) => {
            // While filtering, sections with no surviving rows drop out and the rest force open so the
            // hits are always visible; otherwise it's the normal collapse-by-tap behaviour.
            const vis = visibleEntries(a)
            if (filtering && vis.length === 0) return null
            const isOpen = filtering ? true : open[a.area]
            const areaCounted = vis.filter((e) => rows[e.id]?.counted).length
            return (
              <section key={a.area} className="overflow-hidden rounded-2xl border border-brand-border bg-white">
                <button onClick={() => setOpen((o) => ({ ...o, [a.area]: !o[a.area] }))} className="flex w-full items-center justify-between px-4 py-3 text-left">
                  <span className="flex items-center gap-2 font-semibold text-brand-ink">
                    {isOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                    {a.area}
                  </span>
                  <span className="text-xs text-brand-muted">{areaCounted}/{vis.length}</span>
                </button>
                {isOpen && (
                  <div className="divide-y divide-brand-border">
                    {[...vis].sort((x, y) => (rows[x.id]?.cancelled ? 1 : 0) - (rows[y.id]?.cancelled ? 1 : 0)).map((e0) => {
                      const e = rows[e0.id]
                      const par = parseFloat(e.par_level || '0')
                      const belowPar = e.counted && e.in_stock != null && parseFloat(e.in_stock) <= par
                      // Anomaly hint: counted value wildly different from the last count (>3x or <1/3,
                      // and a meaningful absolute gap) — a subtle "check?" badge, never blocking.
                      const lastc = e.last_counted_in_stock != null ? parseFloat(e.last_counted_in_stock) : null
                      const curN = (e.in_stock != null && e.in_stock !== '') ? parseFloat(e.in_stock) : null
                      const variance = e.counted && curN != null && !isNaN(curN) && lastc != null && lastc > 0 &&
                                       (curN > lastc * 3 || curN < lastc / 3) && Math.abs(curN - lastc) >= 5
                      // Expiry is OPTIONAL — captured if known, but NEVER required and never blocks counting/finishing.
                      return (
                        <div key={e.id} data-stk-row ref={(el) => (rowRefs.current[e.id] = el)} className={cn('px-4 py-3', belowPar && 'bg-amber-50', e.cancelled && 'opacity-60')}>
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex min-w-0 items-start gap-3">
                              <PhotoTile entry={e} finished={finished} busy={uploadingItem === e.stock_item_id}
                                onShoot={() => openCamera(e)} onView={() => setPhotoView(e.id)} />
                              <div className="min-w-0">
                                <p className={cn('truncate font-medium text-brand-ink', e.cancelled && 'text-brand-muted line-through')}>{e.name}{e.counted && <CheckCircle2 size={14} className="ml-1 inline text-emerald-500" />}</p>
                                <p className="text-[11px] text-brand-muted">
                                  {e.unit ? `${e.unit} · ` : ''}par {e.par_level || 0}
                                  {e.last_counted_in_stock != null && ` · last count ${e.last_counted_in_stock}`}
                                  {e.last_ordered_at && ` · ordered ${e.last_ordered_at.slice(0, 10)}`}
                                </p>
                              </div>
                            </div>
                            <div className="flex shrink-0 flex-col items-end gap-1">
                              <div className="flex items-center gap-1">
                                <button type="button" onClick={() => setEditItem(e.id)} aria-label={`edit ${e.name}`} title="Edit item (name, supplier, par…)"
                                  className={cn('flex h-8 w-8 items-center justify-center rounded-lg border active:scale-95',
                                    savedItem === e.stock_item_id ? 'border-emerald-300 bg-emerald-50 text-emerald-600' : 'border-brand-border bg-white text-brand-muted')}>
                                  {savedItem === e.stock_item_id ? <Check size={15} /> : <Pencil size={15} />}
                                </button>
                                <button type="button" onClick={() => setNotesView(e.id)} aria-label={`note for ${e.name}`}
                                  title={e.notes ? 'Note' : 'Add a note'}
                                  className={cn('flex h-8 w-8 items-center justify-center rounded-lg border active:scale-95',
                                    e.notes ? 'border-amber-300 bg-amber-100 text-amber-700' : 'border-brand-border bg-white text-brand-muted')}>
                                  <StickyNote size={15} />
                                </button>
                                <button type="button" onClick={() => setHistoryView(e.id)} aria-label={`history for ${e.name}`} title="History"
                                  className="flex h-8 w-8 items-center justify-center rounded-lg border border-brand-border bg-white text-brand-muted active:scale-95">
                                  <History size={15} />
                                </button>
                                <button type="button" onClick={() => toggleCancel(e)} aria-label={e.cancelled ? `restore ${e.name}` : `cancel ${e.name}`}
                                  title={e.cancelled ? 'Restore item' : 'Cancel item'}
                                  className={cn('flex h-8 w-8 items-center justify-center rounded-lg border active:scale-95',
                                    e.cancelled ? 'border-emerald-300 bg-emerald-50 text-emerald-600' : 'border-brand-border bg-white text-brand-muted')}>
                                  {e.cancelled ? <RotateCcw size={15} /> : <Ban size={15} />}
                                </button>
                              </div>
                              {e.cancelled && <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-semibold text-slate-700">Cancelled</span>}
                              {belowPar && <span className="rounded-full bg-amber-200 px-2 py-0.5 text-[10px] font-semibold text-amber-900">at/below par</span>}
                              {variance && <span title={`Last count was ${e.last_counted_in_stock} — double-check`} className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-semibold text-blue-800">check?</span>}
                            </div>
                          </div>
                          <div className="mt-2 flex items-end gap-3">
                            <div className="flex-1">
                              <span className="block text-[11px] font-medium text-brand-muted">In stock</span>
                              <div className="mt-1 flex items-stretch gap-1.5">
                                <button type="button" disabled={finished} onClick={() => bump(e, -1)} aria-label={`decrease ${e.name}`}
                                  className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl border border-brand-border bg-white text-2xl font-semibold text-brand-ink active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary disabled:opacity-40">−</button>
                                <input inputMode="decimal" disabled={finished} value={e.in_stock ?? ''} onChange={(ev) => setInStock(e, ev.target.value)}
                                  className="h-12 w-full min-w-0 rounded-xl border border-brand-border bg-white px-2 text-center text-lg font-semibold text-brand-ink focus:border-brand-primary focus:outline-none disabled:bg-brand-surface" />
                                <button type="button" disabled={finished} onClick={() => bump(e, 1)} aria-label={`increase ${e.name}`}
                                  className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl border border-brand-border bg-white text-2xl font-semibold text-brand-ink active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary disabled:opacity-40">+</button>
                              </div>
                            </div>
                            <label className="flex-1">
                              <span className="block text-[11px] font-medium text-brand-muted">To order</span>
                              <input inputMode="decimal" disabled={finished} value={e.to_order ?? ''} onChange={(ev) => setToOrder(e, ev.target.value)}
                                className="mt-1 h-12 w-full rounded-xl border border-brand-border bg-brand-surface px-3 text-lg text-brand-ink focus:border-brand-primary focus:outline-none disabled:bg-brand-surface" />
                            </label>
                          </div>
                          {quickPick(e.in_stock, (v) => setInStock(e, v), finished, `quick in-stock count for ${e.name}`)}
                          {/* Expiry (month/year) + stock code. Expiry is FORCED for in-stock items — flagged red until filled. */}
                          <div className="mt-2 flex items-end gap-3">
                            <label className="flex-1">
                              <span className="block text-[11px] font-medium text-brand-muted">Expiry (month, optional)</span>
                              <input type="month" disabled={finished} value={e.expires_on ?? ''} onChange={(ev) => setExpires(e, ev.target.value)}
                                className="mt-1 h-12 w-full rounded-xl border border-brand-border bg-white px-3 text-base text-brand-ink focus:border-brand-primary focus:outline-none disabled:bg-brand-surface" />
                            </label>
                            <label className="flex-1">
                              <span className="block text-[11px] font-medium text-brand-muted">Stock code{!(e.supplier_code) && ' (add it)'}</span>
                              <input type="text" disabled={finished} value={e.supplier_code ?? ''} onChange={(ev) => setSupplierCode(e, ev.target.value)}
                                placeholder="supplier order code"
                                className="mt-1 h-12 w-full rounded-xl border border-brand-border bg-white px-3 text-base text-brand-ink focus:border-brand-primary focus:outline-none disabled:bg-brand-surface" />
                            </label>
                          </div>
                          {/* Par (min stock) — editable at ANY stage (never disabled), it's a catalogue property not a count value.
                              Stepper + quick-pick numbers like In stock (Paul 23 Oct 2026). */}
                          <div className="mt-2">
                            <span className="block text-[11px] font-medium text-brand-muted">Par (min stock) · editable anytime</span>
                            <div className="mt-1 flex max-w-xs items-stretch gap-1.5">
                              <button type="button" onClick={() => bumpPar(e, -1)} aria-label={`decrease minimum stock for ${e.name}`}
                                className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl border border-brand-border bg-white text-2xl font-semibold text-brand-ink active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary">−</button>
                              <input inputMode="decimal" value={e.par_level ?? ''} onChange={(ev) => setPar(e, ev.target.value)}
                                aria-label={`minimum stock level for ${e.name}`} title="Minimum stock you like to keep — editable anytime"
                                className="h-12 w-full min-w-0 rounded-xl border border-brand-border bg-white px-2 text-center text-lg font-semibold text-brand-ink focus:border-brand-primary focus:outline-none" />
                              <button type="button" onClick={() => bumpPar(e, 1)} aria-label={`increase minimum stock for ${e.name}`}
                                className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl border border-brand-border bg-white text-2xl font-semibold text-brand-ink active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary">+</button>
                            </div>
                            {quickPick(e.par_level, (v) => setPar(e, v), false, `quick minimum stock for ${e.name}`)}
                          </div>
                          {/* Supplier: still auto-identified from the stock code, and now EDITABLE by anyone (Paul 23 Oct 2026). */}
                          <p className="mt-2 flex flex-wrap items-center gap-1 text-[11px] text-brand-muted">
                            Supplier:{' '}
                            {e.supplier
                              ? <span className="font-medium text-brand-ink">{e.supplier}</span>
                              : <span className="text-amber-600">not set (or identified from the stock code)</span>}
                            <button type="button" onClick={() => setEditItem(e.id)} aria-label={`edit supplier for ${e.name}`}
                              className="ml-1 inline-flex items-center gap-0.5 rounded-md border border-brand-border bg-white px-1.5 py-0.5 font-medium text-brand-primary active:scale-95">
                              <Pencil size={11} /> edit
                            </button>
                            {savedItem === e.stock_item_id && <span className="inline-flex items-center gap-0.5 font-medium text-emerald-600"><Check size={12} /> saved</span>}
                          </p>
                        </div>
                      )
                    })}
                  </div>
                )}
              </section>
            )
          })}
        </div>

        {filtering && matchCount === 0 && (
          <div className="mt-3 rounded-2xl border border-dashed border-brand-border bg-white px-4 py-10 text-center text-sm text-brand-muted">
            No items match{query.trim() && <> “<span className="font-medium text-brand-ink">{query.trim()}</span>”</>}{activeCat && <> in {activeCat}</>}.
          </div>
        )}
      </div>

      {/* Mobile thumb-zone action bar (the count list is long; keep actions in reach). Desktop uses the header.
          Hidden while the on-screen keyboard is up for a count field: it would sit on the keyboard and cover the row. */}
      {!finished && !kb.open && (
        <div className="fixed inset-x-0 bottom-0 z-20 flex gap-2 border-t border-brand-border bg-white/95 px-4 py-3 backdrop-blur lg:hidden">
          <button onClick={() => setScanOpen(true)} className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-brand-border bg-white py-3 text-sm font-medium text-brand-ink active:scale-[.98]"><ScanLine size={18} /> Scan</button>
          <button onClick={() => { setPrefillName(''); setFlagOpen(true) }} className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-brand-border bg-white py-3 text-sm font-medium text-brand-ink active:scale-[.98]"><PlusCircle size={18} /> Add</button>
          <button onClick={finishTake} className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-brand-primary py-3 text-sm font-semibold text-white active:scale-[.98]"><CheckCircle2 size={18} /> Finish</button>
        </div>
      )}

      {/* The one camera input for the whole page. `capture="environment"` = tablet opens the rear
          camera directly; the OS handles the permission prompt. Works on plain HTTP too (unlike
          getUserMedia), so it keeps working on the LAN/Tailscale hosts, not just the https one. */}
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" onChange={onPhotoPicked} className="hidden" />

      {photoView != null && rows[photoView] && (rows[photoView].photos?.length || rows[photoView].photo_url) && (
        <PhotoViewer entry={rows[photoView]} finished={finished} isOwner={is_owner}
          onClose={() => setPhotoView(null)}
          onAddPhoto={() => openCamera(rows[photoView])}
          onDeletePhoto={(pid) => deletePhoto(rows[photoView], pid)}
          onSavePhotoNote={(pid, note) => savePhotoNote(rows[photoView].stock_item_id, pid, note)} />
      )}
      {notesView != null && rows[notesView] && (
        <NotesEditor entry={rows[notesView]} finished={finished}
          onClose={() => setNotesView(null)}
          onSave={(text) => saveNotes(rows[notesView].stock_item_id, text)} />
      )}
      {historyView != null && rows[historyView] && (
        <HistorySheet title={rows[historyView].name} logs={rows[historyView].logs} onClose={() => setHistoryView(null)} />
      )}
      {flagOpen && <AddItemForm categories={categories} initialBarcode={prefillName} takeId={finished ? null : take.id}
        onAdded={addEntryToTake} onClose={() => setFlagOpen(false)} />}
      {editItem != null && rows[editItem] && (
        <ItemEditor entry={rows[editItem]} categories={categories}
          onClose={() => setEditItem(null)}
          onSave={(patch) => saveDetails(rows[editItem], patch)} />
      )}
      {/* ONE supplier pick-list for the page (known suppliers; typing a new name is always allowed). */}
      <datalist id="stk-supplier-list">{supplierList.map((sname) => <option key={sname} value={sname} />)}</datalist>
      {scanOpen && <CameraScan onClose={() => setScanOpen(false)} onDetected={(code) => { setScanOpen(false); matchBarcode(code) }} />}
      {linkCode && (
        <LinkBarcode code={linkCode} rows={rows}
          onClose={() => setLinkCode(null)}
          onLink={linkBarcodeTo}
          onAddNew={() => { const c = linkCode; setLinkCode(null); setPrefillName(c); setFlagOpen(true) }} />
      )}
    </DashboardLayout>
  )
}

// Tap-to-photograph tile at the head of each row.
//   no photo  → amber dashed camera. The nudge: at a glance you can see which items still need one,
//               and two or three counts fill the catalogue in. It NEVER blocks counting — a 193-item
//               count can't afford a mandatory photo step.
//   has photo → the thumbnail. Tap to enlarge; retake lives inside the viewer, so a good photo is
//               never overwritten by a mis-tap on a moving tablet.
function PhotoTile({ entry, finished, busy, onShoot, onView }) {
  const base = 'flex h-14 w-14 shrink-0 items-center justify-center rounded-xl active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary'
  if (busy) {
    return <div className={cn(base, 'border border-brand-border bg-brand-surface text-brand-muted')}><Loader2 size={20} className="animate-spin" /></div>
  }
  if (entry.photo_url) {
    const extra = (entry.photos?.length || 0) - 1
    return (
      <button type="button" onClick={onView} aria-label={`view photos of ${entry.name}`} className={cn(base, 'relative overflow-hidden border border-brand-border bg-white')}>
        <img src={entry.photo_url} alt={entry.name} loading="lazy" className="h-full w-full object-cover" />
        {extra > 0 && <span className="absolute -right-1 -top-1 rounded-full bg-brand-primary px-1.5 py-px text-[10px] font-bold text-white shadow">+{extra}</span>}
      </button>
    )
  }
  return (
    <button type="button" disabled={finished} onClick={onShoot} aria-label={`take a photo of ${entry.name}`} title="Take a photo of this item"
      className={cn(base, 'border-2 border-dashed border-amber-300 bg-amber-50 text-amber-600 disabled:opacity-40')}>
      <Camera size={20} />
    </button>
  )
}

// Full-screen photo GALLERY — the "is this the right item?" moment. Shows the current photo big, a
// filmstrip of ALL photos (newest first) each labelled with its date, and "Add another photo" (which
// APPENDS via the same camera flow — a stray tap can never destroy a good photo). ANY staff member can delete a
// single photo (the red X, confirmed + audited) while the count is open; the owner always. photos[0] is current.
function PhotoViewer({ entry, finished, isOwner, onClose, onAddPhoto, onDeletePhoto, onSavePhotoNote }) {
  const photos = (entry.photos && entry.photos.length)
    ? entry.photos
    : (entry.photo_url ? [{ id: 0, url: entry.photo_url, taken_at: entry.photo_taken_at }] : [])
  const [selId, setSelId] = useState(photos[0]?.id)
  useEffect(() => {
    if (!photos.some((p) => p.id === selId)) setSelId(photos[0]?.id)
  }, [entry.photos]) // eslint-disable-line react-hooks/exhaustive-deps
  const sel = photos.find((p) => p.id === selId) || photos[0]
  if (!sel) return null
  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black/90 p-4" onClick={onClose}>
      <div className="flex items-start justify-between gap-3 text-white">
        <div className="min-w-0">
          <p className="truncate font-medium">{entry.name}</p>
          <p className="text-xs text-white/60">
            {photos.length} photo{photos.length === 1 ? '' : 's'}
            {sel.taken_at && ` · shown ${sel.taken_at.slice(0, 10)}`}
            {entry.photo_taken_by && ` · latest by ${entry.photo_taken_by}`}
          </p>
        </div>
        <button onClick={onClose} aria-label="close" className="rounded-full p-2 text-white/80 hover:bg-white/10"><X size={22} /></button>
      </div>
      <div className="flex min-h-0 flex-1 items-center justify-center py-3">
        <img src={sel.url} alt={entry.name} className="max-h-full max-w-full rounded-xl object-contain" onClick={(ev) => ev.stopPropagation()} />
      </div>
      {/* per-photo note (everyone): opening a photo shows its note; edit + autosave */}
      {onSavePhotoNote && sel.id !== 0 && (
        <div className="mb-3" onClick={(ev) => ev.stopPropagation()}>
          <PhotoNoteEditor key={sel.id} note={sel.note} onSave={(v) => onSavePhotoNote(sel.id, v)} />
        </div>
      )}
      {/* filmstrip: every photo of this item, tap to enlarge; per-photo delete for staff (open count) + owner */}
      <div className="mb-3 flex gap-3 overflow-x-auto pb-1" onClick={(ev) => ev.stopPropagation()}>
        {photos.map((p) => (
          <div key={p.id} className="shrink-0 text-center">
            <div className="relative">
              <button type="button" onClick={() => setSelId(p.id)}
                className={cn('block h-16 w-16 overflow-hidden rounded-lg border-2', p.id === sel.id ? 'border-white' : 'border-white/25')}>
                <img src={p.url} alt="" className="h-full w-full object-cover" />
              </button>
              {(isOwner || !finished) && p.id !== 0 && (
                <button type="button" onClick={() => onDeletePhoto(p.id)} aria-label="delete this photo"
                  className="absolute -right-1.5 -top-1.5 rounded-full bg-red-600 p-1 text-white shadow active:scale-90">
                  <Trash2 size={12} />
                </button>
              )}
            </div>
            {p.taken_at && <span className="mt-1 block text-[9px] text-white/50">{p.taken_at.slice(0, 10)}</span>}
          </div>
        ))}
      </div>
      {!finished && (
        <button onClick={(ev) => { ev.stopPropagation(); onAddPhoto() }}
          className="mx-auto flex items-center gap-2 rounded-xl bg-white px-5 py-3 text-sm font-semibold text-brand-ink active:scale-95">
          <Camera size={18} /> Add another photo
        </button>
      )}
    </div>
  )
}

// Resize on the TABLET before uploading. The rig's container has no vips/imagemagick, so the browser
// is the only image decoder in the chain — and we want it anyway: a raw camera file is 3-5 MB, which
// is slow over practice WiFi and would make a 193-row count screen unloadable. ~1000 px at JPEG 0.75
// is ≈150 KB: sharp enough to read a product label, cheap enough to lazy-load a whole count.
// Decoding via <img> (not createImageBitmap) so an iPad HEIC still works — it uses the system decoder.
async function downscale(file, maxPx = 1000, quality = 0.75) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      const scale = Math.min(1, maxPx / Math.max(img.width, img.height))
      const w = Math.max(1, Math.round(img.width * scale))
      const h = Math.max(1, Math.round(img.height * scale))
      const canvas = document.createElement('canvas')
      canvas.width = w
      canvas.height = h
      canvas.getContext('2d').drawImage(img, 0, 0, w, h)
      URL.revokeObjectURL(url)
      // If the re-encode somehow came out bigger (tiny images), send the original rather than bloat it.
      canvas.toBlob((b) => resolve(b && b.size < file.size ? b : file), 'image/jpeg', quality)
    }
    // Decode failed (an exotic format on an old browser) — upload the original untouched. The server
    // takes anything image/* under 8 MB, so the photo still lands; it's just heavier.
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file) }
    img.src = url
  })
}

// A category filter pill: label + a small count badge ("done/total" for a real category, or just the
// total for "All"). Highlighted when it's the active filter.
function Chip({ label, count, done, active, onClick }) {
  return (
    <button type="button" onClick={onClick}
      className={cn('flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors active:scale-95',
        active ? 'border-brand-primary bg-brand-primary text-white' : 'border-brand-border bg-white text-brand-ink hover:bg-brand-surface')}>
      <span className="whitespace-nowrap">{label}</span>
      <span className={cn('rounded-full px-1.5 text-[10px] font-semibold',
        active ? 'bg-white/20 text-white' : 'bg-brand-surface text-brand-muted')}>
        {done != null ? `${done}/${count}` : count}
      </span>
    </button>
  )
}

// The saved indicator (stockresume1023): "All saved ✓ 09:49" / "Saving…" / "3 changes not yet saved, retrying".
function SyncBadge({ state, at, pending = 0 }) {
  const n = pending === 1 ? '1 change' : `${pending} changes`
  if (state === 'error') return <span className="flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-bold text-red-700"><CloudOff size={14} /> {pending ? `${n} not yet saved, retrying` : 'NOT SAVED, retrying'}</span>
  if (state === 'saving') return <span className="flex items-center gap-1 text-xs text-brand-muted"><Loader2 size={14} className="animate-spin" /> Saving…</span>
  if (state === 'offline') return <span className="flex items-center gap-1 text-xs text-amber-600"><CloudOff size={14} /> offline — {pending ? `${n} held on this device` : 'will sync'}</span>
  const t = at ? at.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : null
  return <span className="flex items-center gap-1 text-xs text-emerald-600"><Cloud size={14} /> All saved ✓{t ? ` ${t}` : ''}</span>
}

// ADD an item straight into the catalogue — ANY counter, not just the owner (backend #create is
// counter-reachable, CSRF-skipped). ref + name + category are required (storage_area defaults). On an OPEN count
// (takeId) the server also adds it to THIS take at once and returns the row (Paul 23 Oct 2026: "When I click add
// item, it should add it to the immediate stock take"); an item already in the catalogue joins the take instead of
// being duplicated. An optional photo is uploaded straight after via the same POST …/photo append.
function AddItemForm({ categories = [], initialBarcode, takeId, onAdded, onClose }) {
  const [f, setF] = useState({
    ref: '', name: '', category: '',
    supplier: '', unit: '', par_level: '', barcode: initialBarcode || '',
  })
  const [photo, setPhoto] = useState(null)
  const [busy, setBusy] = useState(false)
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }))

  async function submit() {
    if (!f.ref.trim() || !f.name.trim() || !f.category.trim()) { toast.error('Ref, name and category are required'); return }
    if (!navigator.onLine) { toast.error("You're offline — reconnect to add an item."); return }
    setBusy(true)
    try {
      const res = await fetch('/stock_items', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken(), Accept: 'application/json' },
        body: JSON.stringify({ stock_item: {
          ref: f.ref.trim(), name: f.name.trim(), category: f.category.trim(),
          supplier: f.supplier.trim() || null, unit: f.unit.trim() || null,
          par_level: f.par_level.trim() || null, barcode: f.barcode.trim() || null, active: true,
        }, stock_take_id: takeId || null }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.ok) throw new Error(data.error || `HTTP ${res.status}`)
      let entry = data.entry || null
      if (photo && data.item?.id && !data.existing) {
        try {
          const blob = await downscale(photo)
          const fd = new FormData(); fd.append('photo', blob, 'stock.jpg')
          const pr = await fetch(`/stock_items/${data.item.id}/photo`, { method: 'POST', headers: { 'X-CSRF-Token': csrfToken(), Accept: 'application/json' }, body: fd })
          const pd = await pr.json().catch(() => ({}))
          if (pr.ok && pd.ok && entry) entry = { ...entry, photo_url: pd.photo_url, photos: pd.photos, photo_taken_at: pd.photo_taken_at, photo_taken_by: pd.photo_taken_by }
        } catch { /* the item is saved; a failed photo is not worth blocking the add */ }
      }
      const nm = data.item?.name || f.name.trim()
      if (entry && onAdded) {
        onAdded(entry, data.area)
        toast.success(data.existing ? `“${nm}” is already in the catalogue — added to this count.` : `Added “${nm}” to this count.`)
      } else {
        toast.success(`Added “${nm}” — it'll be in the next count.`)
      }
      onClose()
    } catch (err) {
      toast.error(err.message || 'Could not add — try again')
    } finally { setBusy(false) }
  }

  return (
    <Modal onClose={onClose} title="Add a new item">
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <Field label="Ref *"><input autoFocus value={f.ref} onChange={set('ref')} className={INPUT} /></Field>
          <Field label="Category *">
            <input list="stk-cat-list" value={f.category} onChange={set('category')} placeholder="e.g. Restorative" className={INPUT} />
            <datalist id="stk-cat-list">{(categories || []).map((c) => <option key={c} value={c} />)}</datalist>
          </Field>
        </div>
        <Field label="Item name *"><input value={f.name} onChange={set('name')} className={INPUT} /></Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Supplier (optional)"><input list="stk-supplier-list" value={f.supplier} onChange={set('supplier')} className={INPUT} /></Field>
          <Field label="Unit (optional)"><input value={f.unit} onChange={set('unit')} className={INPUT} /></Field>
          <Field label="Par level (optional)"><input inputMode="decimal" value={f.par_level} onChange={set('par_level')} className={INPUT} /></Field>
          <Field label="Barcode (optional)"><input value={f.barcode} onChange={set('barcode')} className={INPUT} /></Field>
        </div>
        <Field label="Photo (optional)"><input type="file" accept="image/*" capture="environment" onChange={(e) => setPhoto(e.target.files?.[0] || null)} className="text-base" /></Field>
        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} className="rounded-xl border border-brand-border px-4 py-2 text-sm">Cancel</button>
          <button onClick={submit} disabled={busy} className="rounded-xl bg-brand-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">{busy ? 'Adding…' : 'Add item'}</button>
        </div>
      </div>
    </Modal>
  )
}

// The count page groups by CATEGORY, so a category edit must regroup the row (stockfix1023b, Codex 6). Pure: every
// row of `stockItemId` leaves its section and goes to the TOP of `toArea` (a new section is added at the top);
// a section the move empties drops out. Nothing to move = the same list back.
function regroupItemRows(list, stockItemId, toArea) {
  const moving = []
  const out = []
  list.forEach((a) => {
    if (a.area === toArea) { out.push(a); return }
    const stay = a.entries.filter((e) => e.stock_item_id !== stockItemId)
    if (stay.length === a.entries.length) { out.push(a); return }
    moving.push(...a.entries.filter((e) => e.stock_item_id === stockItemId))
    if (stay.length > 0) out.push({ ...a, entries: stay })
  })
  if (moving.length === 0) return list
  const idx = out.findIndex((a) => a.area === toArea)
  if (idx === -1) return [{ area: toArea, entries: moving }, ...out]
  return out.map((a, i) => (i === idx ? { ...a, entries: [...moving, ...a.entries] } : a))
}

// EDIT AN ITEM from the count page — ANY staff member (Paul 23 Oct 2026: "everything must be editable"). Sends ONLY
// the fields that changed to PATCH /stock_items/:id/details; the server validates (name/ref required, par a number,
// barcode one-item-only) and audits every change old → new. Deactivating an item stays owner-only (Catalogue).
const EDIT_FIELDS = [
  ['name', 'Item name *'], ['ref', 'Ref *'], ['item_category', 'Category'], ['storage_area', 'Storage area'],
  ['unit', 'Unit'], ['par_level', 'Par (min stock)'], ['supplier', 'Supplier'], ['supplier_code', 'Supplier / stock code'],
  ['barcode', 'Barcode'],
]
function ItemEditor({ entry, categories = [], onClose, onSave }) {
  const start = useMemo(() => Object.fromEntries(EDIT_FIELDS.map(([k]) => [k, entry[k] == null ? '' : String(entry[k])])), []) // eslint-disable-line react-hooks/exhaustive-deps
  const [f, setF] = useState(start)
  const [state, setState] = useState('idle') // idle | saving | saved
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }))
  async function save() {
    if (!f.name.trim() || !f.ref.trim()) { toast.error('Name and ref are required'); return }
    const patch = {}
    EDIT_FIELDS.forEach(([k]) => {
      if (f[k].trim() !== start[k].trim()) patch[k === 'item_category' ? 'category' : k] = f[k].trim()
    })
    if (Object.keys(patch).length === 0) { onClose(); return }
    setState('saving')
    const ok = await onSave(patch)
    if (ok) { setState('saved'); setTimeout(onClose, 700) } else { setState('idle') }
  }
  return (
    <Modal onClose={onClose} title="Edit item">
      <div className="space-y-3">
        <Field label="Item name *"><input autoFocus value={f.name} onChange={set('name')} className={INPUT} /></Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Ref *"><input value={f.ref} onChange={set('ref')} className={INPUT} /></Field>
          <Field label="Category">
            <input list="stk-edit-cat-list" value={f.item_category} onChange={set('item_category')} className={INPUT} />
            <datalist id="stk-edit-cat-list">{(categories || []).map((c) => <option key={c} value={c} />)}</datalist>
          </Field>
          <Field label="Supplier"><input list="stk-supplier-list" value={f.supplier} onChange={set('supplier')} placeholder="pick or type a new one" className={INPUT} /></Field>
          <Field label="Supplier / stock code"><input value={f.supplier_code} onChange={set('supplier_code')} className={INPUT} /></Field>
          <Field label="Storage area"><input value={f.storage_area} onChange={set('storage_area')} className={INPUT} /></Field>
          <Field label="Unit"><input value={f.unit} onChange={set('unit')} className={INPUT} /></Field>
          <Field label="Par (min stock)"><input inputMode="decimal" value={f.par_level} onChange={set('par_level')} className={INPUT} /></Field>
          <Field label="Barcode"><input value={f.barcode} onChange={set('barcode')} className={INPUT} /></Field>
        </div>
        <p className="text-[11px] text-brand-muted">Every change is logged with your name. Removing an item from the catalogue is done by the owner.</p>
        <div className="flex items-center justify-end gap-2 pt-1">
          {state === 'saved' && <span className="flex items-center gap-1 text-sm font-medium text-emerald-600"><Check size={16} /> Saved</span>}
          <button onClick={onClose} className="rounded-xl border border-brand-border px-4 py-2 text-sm">Cancel</button>
          <button onClick={save} disabled={state === 'saving'} className="rounded-xl bg-brand-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">{state === 'saving' ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </Modal>
  )
}

// Persistent NOTES block on an item — everyday counter action (NOT owner-gated). Autosaves on a ~800ms
// debounce AND on blur; shows who last saved it and when. The note is on the catalogue item, so once
// saved it shows on every future count and on the catalogue.
function NotesEditor({ entry, finished, onClose, onSave }) {
  const [text, setText] = useState(entry.notes || '')
  const [state, setState] = useState('idle') // idle | saving | saved
  const timer = useRef(null)
  const lastSaved = useRef(entry.notes || '')

  const doSave = useCallback(async (val) => {
    if (val === lastSaved.current) return
    setState('saving')
    const ok = await onSave(val)
    if (ok) { lastSaved.current = val; setState('saved') } else { setState('idle') }
  }, [onSave])

  function onChange(v) {
    setText(v)
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => doSave(v), 800)
  }
  function flushSave() { if (timer.current) clearTimeout(timer.current); doSave(text) }
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current) }, [])

  return (
    <Modal onClose={() => { flushSave(); onClose() }} title="Item note">
      <div className="space-y-2">
        <p className="text-sm font-medium text-brand-ink">{entry.name}</p>
        <textarea autoFocus disabled={finished} value={text} onChange={(e) => onChange(e.target.value)} onBlur={flushSave} rows={5}
          placeholder="e.g. the 100 ml one · kept in the back cupboard · supplier discontinued the blue box"
          className={INPUT} />
        <div className="flex items-center justify-between text-[11px] text-brand-muted">
          <span>{state === 'saving' ? 'saving…' : state === 'saved' ? 'saved' : (entry.notes_updated_by ? `${entry.notes_updated_by} · ${(entry.notes_updated_at || '').slice(0, 10)}` : 'no note yet')}</span>
          <span>{text.length}/4000</span>
        </div>
        <div className="flex justify-end pt-1">
          <button onClick={() => { flushSave(); onClose() }} className="rounded-xl bg-brand-primary px-4 py-2 text-sm font-semibold text-white">Done</button>
        </div>
      </div>
    </Modal>
  )
}

// A scanned barcode matched no item yet. Rather than only "add new", the counter LINKS it to the
// existing item it belongs to — that fills the catalogue's barcodes over normal counts, so the NEXT scan
// of that box resolves straight to the right item. Server enforces one-barcode-one-item; a clash offers
// an explicit reassign. "Not listed" still opens the add-new form (with the code prefilled).
function LinkBarcode({ code, rows, onClose, onLink, onAddNew }) {
  const [q, setQ] = useState('')
  const [busy, setBusy] = useState(false)
  const [conflict, setConflict] = useState(null) // {id, name} the code is already on
  const [pending, setPending] = useState(null)    // item awaiting a forced reassign confirm
  const items = useMemo(() => {
    const seen = new Set(); const list = []
    Object.values(rows).forEach((r) => { if (!seen.has(r.stock_item_id)) { seen.add(r.stock_item_id); list.push(r) } })
    const needle = q.trim().toLowerCase()
    const f = needle === '' ? list
      : list.filter((r) => `${r.name || ''} ${r.ref || ''} ${r.supplier || ''} ${r.supplier_code || ''}`.toLowerCase().includes(needle))
    return f.slice(0, 40)
  }, [rows, q])

  async function pick(item, force = false) {
    setBusy(true)
    const out = await onLink(item.stock_item_id, force)
    setBusy(false)
    if (out && out.conflict) { setConflict(out.conflict); setPending(item) }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center" onClick={onClose}>
      <div className="w-full max-w-md rounded-t-2xl bg-brand-surface p-4 sm:rounded-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-2 flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-base font-semibold text-brand-ink"><Link2 size={18} /> Link barcode</h2>
          <button onClick={onClose} className="rounded-full p-1 text-brand-muted hover:bg-white active:scale-90"><X size={18} /></button>
        </div>
        <p className="mb-3 text-sm text-brand-muted">Scanned <span className="font-mono font-semibold text-brand-ink">{code}</span> — which item is this?</p>
        {conflict ? (
          <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm">
            <p className="text-brand-ink">Already linked to <span className="font-semibold">{conflict.name}</span>.</p>
            <div className="mt-3 flex gap-2">
              <button disabled={busy} onClick={() => pick(pending, true)} className="flex-1 rounded-xl bg-amber-600 py-2 text-sm font-semibold text-white active:scale-95 disabled:opacity-50">Move it to “{pending?.name}”</button>
              <button onClick={() => { setConflict(null); setPending(null) }} className="flex-1 rounded-xl border border-brand-border bg-white py-2 text-sm font-medium text-brand-ink active:scale-95">Keep as is</button>
            </div>
          </div>
        ) : (
          <>
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search item, code, supplier…"
              className="mb-2 w-full rounded-xl border border-brand-border bg-white px-3 py-2.5 text-base text-brand-ink focus:border-brand-primary focus:outline-none" />
            <div className="max-h-64 divide-y divide-brand-border overflow-y-auto rounded-xl border border-brand-border bg-white">
              {items.length === 0 && <p className="p-3 text-sm text-brand-muted">No match — add it as a new item below.</p>}
              {items.map((r) => (
                <button key={r.stock_item_id} disabled={busy} onClick={() => pick(r)}
                  className="flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left text-sm text-brand-ink hover:bg-brand-surface active:scale-[.99] disabled:opacity-50">
                  <span className="truncate"><span className="font-medium">{r.name}</span>{r.supplier ? <span className="text-brand-muted"> · {r.supplier}</span> : null}{r.barcode ? <span className="text-xs text-amber-600"> · has barcode</span> : null}</span>
                  <Link2 size={15} className="shrink-0 text-brand-primary" />
                </button>
              ))}
            </div>
            <button onClick={onAddNew} className="mt-3 w-full rounded-xl border border-dashed border-brand-border bg-white py-2.5 text-sm font-medium text-brand-ink active:scale-95">＋ Not listed — add as a new item</button>
          </>
        )}
      </div>
    </div>
  )
}

// Camera barcode scan via the native BarcodeDetector API where available; graceful manual fallback.
function CameraScan({ onClose, onDetected }) {
  const videoRef = useRef(null)
  const [manual, setManual] = useState('')
  const [supported, setSupported] = useState(true)
  useEffect(() => {
    let stream, raf, detector, stop = false
    async function start() {
      if (!('BarcodeDetector' in window)) { setSupported(false); return }
      try {
        detector = new window.BarcodeDetector()
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } })
        if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play() }
        const tick = async () => {
          if (stop) return
          try {
            const codes = await detector.detect(videoRef.current)
            if (codes && codes[0]?.rawValue) { onDetected(codes[0].rawValue); return }
          } catch {}
          raf = requestAnimationFrame(tick)
        }
        tick()
      } catch { setSupported(false) }
    }
    start()
    return () => { stop = true; if (raf) cancelAnimationFrame(raf); stream?.getTracks().forEach((t) => t.stop()) }
  }, [onDetected])

  return (
    <Modal onClose={onClose} title="Scan barcode">
      {supported ? (
        <video ref={videoRef} className="aspect-video w-full rounded-xl bg-black" muted playsInline />
      ) : (
        <p className="text-sm text-brand-muted">Camera scanning isn't available on this device. Use a USB/Bluetooth scanner (it types into the page), or enter the barcode below.</p>
      )}
      <div className="mt-3 flex gap-2">
        <input value={manual} onChange={(e) => setManual(e.target.value)} placeholder="Type / paste barcode" className={cn(INPUT, "flex-1")} />
        <button onClick={() => manual.trim() && onDetected(manual.trim())} className="rounded-xl bg-brand-primary px-4 text-sm font-semibold text-white">Find</button>
      </div>
    </Modal>
  )
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-t-2xl bg-white p-4 shadow-xl sm:rounded-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-semibold text-brand-ink">{title}</h3>
          <button onClick={onClose} aria-label="Close" className="rounded-lg p-1 text-brand-muted hover:bg-brand-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  )
}

function Field({ label, children }) {
  return <label className="block"><span className="mb-1 block text-xs font-medium text-brand-muted">{label}</span>{children}</label>
}

// Per-photo note inside the viewer — opening a photo shows its note; edit it and it autosaves on a
// ~800ms debounce AND on blur. Everyone can write it; the returned photos[] fans out to every row.
function PhotoNoteEditor({ note, onSave }) {
  const [text, setText] = useState(note || '')
  const [state, setState] = useState('idle') // idle | saving | saved
  const timer = useRef(null)
  const lastSaved = useRef(note || '')
  const doSave = useCallback(async (val) => {
    if (val === lastSaved.current) return
    setState('saving')
    const ok = await onSave(val)
    if (ok) { lastSaved.current = val; setState('saved') } else { setState('idle') }
  }, [onSave])
  function onChange(v) {
    setText(v)
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => doSave(v), 800)
  }
  function flushSave() { if (timer.current) clearTimeout(timer.current); doSave(text) }
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current) }, [])
  return (
    <div className="rounded-xl bg-white/10 p-2">
      <textarea value={text} onChange={(e) => onChange(e.target.value)} onBlur={flushSave} rows={2}
        placeholder="Note for this photo — e.g. front label · batch / lot no. · which variant"
        className="w-full resize-none rounded-lg border border-white/20 bg-black/30 px-3 py-2 text-base text-white placeholder-white/40 focus:border-white/50 focus:outline-none" />
      <div className="mt-1 text-right text-[11px] text-white/50">{state === 'saving' ? 'saving…' : state === 'saved' ? 'saved' : 'photo note'}</div>
    </div>
  )
}

// ── per-item history timeline (read-only, append-only log) ─────────────────
const LOG_META = {
  note:             { icon: StickyNote,    label: 'Note' },
  supplier_changed: { icon: Truck,         label: 'Supplier changed' },
  edited:           { icon: Pencil,        label: 'Edited' },
  photo_added:      { icon: Camera,        label: 'Photo added' },
  photo_removed:    { icon: Trash2,        label: 'Photo removed' },
  created:          { icon: PlusCircle,    label: 'Created' },
  cancelled:        { icon: Ban,           label: 'Cancelled' },
  restored:         { icon: RotateCcw,     label: 'Restored' },
  photo_noted:      { icon: MessageSquare, label: 'Photo note' },
}

function friendlyAt(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  if (isNaN(d.getTime())) return String(iso).slice(0, 16).replace('T', ' ')
  return d.toLocaleString([], { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
}

// Read-only timeline for one item: newest-first, kind-appropriate icon + body + who + friendly time.
function HistorySheet({ title, logs, onClose }) {
  const list = logs || []
  return (
    <Modal onClose={onClose} title="History">
      <p className="mb-3 truncate text-sm font-medium text-brand-ink">{title}</p>
      {list.length === 0 ? (
        <p className="py-6 text-center text-sm text-brand-muted">No history yet.</p>
      ) : (
        <ul className="max-h-[60vh] space-y-3 overflow-y-auto pr-1">
          {list.map((l, idx) => {
            const meta = LOG_META[l.kind] || { icon: History, label: l.kind }
            const Icon = meta.icon
            return (
              <li key={idx} className="flex gap-3">
                <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand-surface text-brand-muted"><Icon size={14} /></span>
                <div className="min-w-0">
                  <p className="text-sm text-brand-ink">{l.body || meta.label}</p>
                  <p className="text-[11px] text-brand-muted">{[l.actor, friendlyAt(l.at)].filter(Boolean).join(' · ')}</p>
                </div>
              </li>
            )
          })}
        </ul>
      )}
    </Modal>
  )
}

// ── KEYBOARD-SAFE TYPING (stockview1023 — Paul 23 Oct 2026, live count on a tablet: "when we type, we can still see
// everything"). Touch devices only, so a desktop is untouched (and has no keyboard shrink anyway).
//  • THIS page only: the viewport meta gains interactive-widget=resizes-content (Android Chrome shrinks the layout for
//    the keyboard); the original string goes back on unmount. Pinch-zoom is never disabled.
//  • Only a NEW focus on a typing field inside `listRef`, or the viewport shrinking while it is focused, moves the page:
//    the field goes into the band between the sticky header and the keyboard, with the row's name showing if it fits.
//    ± / quick-pick are buttons, so they never trigger it; a finger scrolling the page cancels any pending move.
//  • Returns { open, pad }: pad = keyboard height when the layout did NOT shrink (iPad), for bottom padding so the
//    last rows can still scroll clear of the keyboard.
const NOT_TYPING = /^(button|checkbox|radio|file|range|submit|reset|color|hidden|image)$/i
function isTypingField(el) {
  if (!el || el.disabled || el.readOnly) return false
  if (el.tagName === 'TEXTAREA' || el.tagName === 'SELECT') return true
  return el.tagName === 'INPUT' && !NOT_TYPING.test(el.type || 'text')
}

function useKeyboardSafeFocus(listRef, headerRef) {
  const [kb, setKb] = useState({ open: false, pad: 0 })

  useEffect(() => {
    const meta = document.querySelector('meta[name="viewport"]')
    const orig = meta ? meta.getAttribute('content') : null
    if (orig != null && !/interactive-widget/i.test(orig)) meta.setAttribute('content', `${orig}, interactive-widget=resizes-content`)
    return () => { if (orig != null) meta.setAttribute('content', orig) }
  }, [])

  useEffect(() => {
    const vv = window.visualViewport
    const touch = (navigator.maxTouchPoints || 0) > 0 || !!window.matchMedia?.('(pointer: coarse)').matches
    if (!vv || !touch) return undefined
    const KB_MIN = 120          // px of shrink that means a keyboard (a browser toolbar collapsing is less)
    let focused = null          // the count field being kept in view
    let fresh = false           // focused, but not placed yet
    let fullH = Math.max(vv.height, window.innerHeight) // keyboard-closed height, re-learned when nothing is typed in
    let width = window.innerWidth
    let lastH = vv.height
    let lastHandScroll = 0
    let touchY = null
    let timer = null
    let blurTimer = null        // the 120 ms post-blur sync (tracked so unmount can cancel it — audit 23 Oct)

    const kbHeight = () => Math.max(fullH, window.innerHeight) - vv.height
    const kbOpen = () => !!focused && vv.scale < 1.05 && kbHeight() > KB_MIN // pinch-zoomed = the user's choice, leave it
    let lastOpen = false        // keyboard state at the previous event (a rotation needs to know whether it was up)
    const sync = () => {
      const open = kbOpen()
      lastOpen = open
      const pad = open ? Math.max(0, Math.round(window.innerHeight - vv.height)) : 0
      setKb((k) => (k.open === open && k.pad === pad ? k : { open, pad }))
    }
    const place = () => {
      timer = null
      const el = focused
      if (!el || !el.isConnected || document.activeElement !== el || !kbOpen()) return
      if (Date.now() - lastHandScroll < 400) return // never fight a finger
      fresh = false
      const head = headerRef.current ? headerRef.current.getBoundingClientRect().bottom : 64
      const top = Math.max(vv.offsetTop, head) + 8          // below the fixed nav + sticky header/search
      const bottom = vv.offsetTop + vv.height - 12          // above the keyboard
      const f = el.getBoundingClientRect()
      const rowTop = (el.closest('[data-stk-row]') || el).getBoundingClientRect().top
      const showName = rowTop - top                         // scroll that puts the row's name at the top of the band
      const nameFits = f.bottom - showName <= bottom
      if (f.top >= top && f.bottom <= bottom && (rowTop >= top || !nameFits)) return // already fine: don't move
      const d = nameFits ? showName : (f.top + f.bottom) / 2 - (top + bottom) / 2    // else centre the field
      if (Math.abs(d) > 2) window.scrollBy({ top: d, behavior: 'smooth' })
    }
    const schedule = (ms) => { clearTimeout(timer); timer = setTimeout(place, ms) }

    const onFocusIn = (ev) => {
      const t = ev.target
      if (!isTypingField(t) || !listRef.current || !listRef.current.contains(t)) return
      focused = t
      fresh = true
      sync()
      schedule(kbOpen() ? 60 : 320) // keyboard already up → place now; else after it has animated in
    }
    const onFocusOut = (ev) => {
      if (ev.target !== focused) return
      focused = null
      fresh = false
      clearTimeout(timer)
      clearTimeout(blurTimer)
      blurTimer = setTimeout(sync, 120) // moving to the next field re-focuses first, so the thumb bar doesn't flicker
    }
    const onResize = () => {
      if (window.innerWidth !== width) {
        width = window.innerWidth
        // Rotation. Android keeps the keyboard up through it, so the shrunken height must NOT become the "closed"
        // baseline (it did: kbOpen() went false and the field was left hidden — stress test h, audit 23 Oct). While it
        // is up the closed height is only known to be more than a keyboard above vv; a blur + resize relearns it.
        fullH = lastOpen ? vv.height + KB_MIN + 1 : Math.max(vv.height, window.innerHeight)
        if (focused) fresh = true // place the field again in the new orientation
      }
      else if (!isTypingField(document.activeElement) && vv.scale < 1.05) fullH = Math.max(vv.height, window.innerHeight)
      // Any real shrink re-checks the field (place() bails if it is still fully in the band, and the hand-scroll
      // guard still applies), so several small steps — a suggestion strip growing — cannot creep over it (audit 23 Oct).
      const shrank = vv.height < lastH - 4
      lastH = vv.height
      sync()
      if (focused && (fresh || shrank)) schedule(80)
    }
    const onTouchStart = (ev) => { touchY = ev.touches[0] ? ev.touches[0].clientY : null }
    const onTouchMove = (ev) => {
      if (touchY == null || !ev.touches[0] || Math.abs(ev.touches[0].clientY - touchY) < 10) return // a tap, not a scroll
      lastHandScroll = Date.now()
      clearTimeout(timer)
      timer = null
    }

    document.addEventListener('focusin', onFocusIn)
    document.addEventListener('focusout', onFocusOut)
    vv.addEventListener('resize', onResize)
    window.addEventListener('touchstart', onTouchStart, { passive: true })
    window.addEventListener('touchmove', onTouchMove, { passive: true })
    return () => {
      clearTimeout(timer)
      clearTimeout(blurTimer)
      document.removeEventListener('focusin', onFocusIn)
      document.removeEventListener('focusout', onFocusOut)
      vv.removeEventListener('resize', onResize)
      window.removeEventListener('touchstart', onTouchStart)
      window.removeEventListener('touchmove', onTouchMove)
    }
  }, [listRef, headerRef])

  return kb
}

// ── localStorage queue helpers ──
function loadQueue(k) { try { return JSON.parse(localStorage.getItem(k) || '{}') } catch { return {} } }
function saveQueue(k, q) { try { localStorage.setItem(k, JSON.stringify(q)) } catch {} }
function stripId({ id, ...rest }) { return rest }
function shallowEqual(a, b) { if (!a || !b) return false; const ka = Object.keys(a), kb = Object.keys(b); if (ka.length !== kb.length) return false; return ka.every((k) => String(a[k]) === String(b[k])) }
